import { useState } from "react";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT, type Locale } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import {
  DSAR_CHANNELS,
  DSAR_REQUEST_TYPES,
  DSAR_STATUSES,
  DSR_ERASE_ACTION,
  isOverdue,
  manualTransitions,
  type CreateDsarInput,
  type DsarChannel,
  type DsarFilter,
  type DsarRequest,
  type DsarRequestType,
  type DsarStatus,
  type EraseResult,
} from "../../api/dsar-contracts";
import type { CustomerSearchBy, PartySummary } from "../../api/party-contracts";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  StatusLabel,
  Table,
  TableCard,
  Toast,
  ToastStack,
  type StatusTone,
} from "../../ui";
import { useCustomerSearch } from "../customers/use-customers";
import {
  useCreateDsar,
  useDsarList,
  useExecuteErasure,
  useMyApprovals,
  useRequestErasureApproval,
  useUpdateDsarStatus,
} from "./use-dsar";

// KVKK — Veri-sahibi talepleri (DSAR register). A compliance-desk surface: log a
// data-subject request (POST, due_at = received_at + 30 days server-side), list the
// register (GET, server-side status/type filters + "daha fazla" keyset pagination) and
// advance a request's handling status (PATCH, forward-legal transitions only). RBAC is
// UX: dsar:read gates the list, dsar:write gates the create form + status controls; the
// API re-enforces every call regardless.
//
// GAP-AUDIT-3 D1 — the ERASURE lane. POST /v1/admin/dsar/:id/execute-erasure (built in
// #227: four-eyes `dsr.erase` bound to the DSAR id + step-up, running DsrService.erase)
// existed for a whole release with ZERO references in this app. The desk's only control
// was the plain status PATCH, so an operator could mark a KVKK erasure FULFILLED while
// nothing was shredded — a compliance record asserting a deletion that never happened.
// Two changes close it: FULFILLED is removed from the hand-driven transitions for an
// ERASURE row (`manualTransitions`), and the real endpoint gets a first-class lane with
// its approval cycle, an irreversibility confirmation, and the shred receipt.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TYPE_LABEL_KEY: Record<DsarRequestType, MessageKey> = {
  ACCESS: "dsar.type.ACCESS",
  ERASURE: "dsar.type.ERASURE",
  RECTIFICATION: "dsar.type.RECTIFICATION",
  OBJECTION: "dsar.type.OBJECTION",
  PORTABILITY: "dsar.type.PORTABILITY",
};

const CHANNEL_LABEL_KEY: Record<DsarChannel, MessageKey> = {
  EMAIL: "dsar.channel.EMAIL",
  PHONE: "dsar.channel.PHONE",
  POST: "dsar.channel.POST",
  IN_PERSON: "dsar.channel.IN_PERSON",
  WEB: "dsar.channel.WEB",
};

const STATUS_LABEL_KEY: Record<DsarStatus, MessageKey> = {
  RECEIVED: "dsar.status.RECEIVED",
  IN_PROGRESS: "dsar.status.IN_PROGRESS",
  FULFILLED: "dsar.status.FULFILLED",
  REJECTED: "dsar.status.REJECTED",
};

// Calm status tones from the existing kit tokens (no new palette): a newly-logged
// request is neutral, in-progress is the review tone, fulfilled is the calm green, a
// rejected request wears the DS's terminal tone. Overdue gets a separate amber hint.
const STATUS_TONE: Record<DsarStatus, StatusTone> = {
  RECEIVED: "pending",
  IN_PROGRESS: "review",
  FULFILLED: "active",
  REJECTED: "ended",
};

function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    dateStyle: "medium",
  }).format(new Date(iso));
}

/** Map a mutation ProblemError to a calm, typed message key (branch on the stable
 *  code/status, never on human text). 409 = an illegal status transition. */
function mutationErrorKey(err: unknown): MessageKey | null {
  if (!(err instanceof ProblemError)) return null;
  // The erasure lane's two governance denials get their own copy — an operator
  // told only "not authorized" would not know to fetch a second approver or
  // re-authenticate, and would fall back to closing the request by hand.
  if (err.is(ErrorCodes.FOUR_EYES_REQUIRED)) return "dsar.error.fourEyes";
  if (err.is(ErrorCodes.STEP_UP_REQUIRED)) return "dsar.error.stepUp";
  if (err.is(ErrorCodes.FORBIDDEN)) return "dsar.error.forbidden";
  if (err.is(ErrorCodes.NOT_FOUND)) return "dsar.error.notFound";
  if (err.is(ErrorCodes.CONFLICT)) return "dsar.error.conflict";
  if (err.is(ErrorCodes.VALIDATION_FAILED)) return "dsar.error.validation";
  return "dsar.error.generic";
}

export function Dsar() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canWrite = hasGrant(me, "dsar:write", "all");
  const canRead = hasGrant(me, "dsar:read", "all");
  // The subject-party picker rides the customers /v1/parties/find endpoint — offered
  // only when the operator actually holds party:read (else the uuid input stands alone).
  const canFindParty = hasGrant(me, "party:read", "own");

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("dsar.heading")}</h1>
          <p className="surf-sub muted">{t("dsar.sub")}</p>
        </div>
      </div>

      {canWrite && <CreateDsar canFindParty={canFindParty} />}
      {!canWrite && <p className="muted surf-note">{t("dsar.readOnly")}</p>}

      {canRead && <DsarList canWrite={canWrite} />}
    </section>
  );
}

// The create form: subject party (a uuid input, optionally filled from the party
// search), request type, channel, an optional note. due_at is server-derived — shown as
// an info line. Fulfilling an ERASURE request triggers the separate crypto-shred; a calm
// reminder appears when ERASURE is selected.
function CreateDsar({ canFindParty }: { canFindParty: boolean }) {
  const { t } = useT();
  const create = useCreateDsar();

  const [subjectPartyId, setSubjectPartyId] = useState("");
  const [subjectLabel, setSubjectLabel] = useState<string | null>(null);
  const [requestType, setRequestType] = useState<DsarRequestType>("ACCESS");
  const [channel, setChannel] = useState<DsarChannel>("EMAIL");
  const [note, setNote] = useState("");

  const idValid = UUID_RE.test(subjectPartyId.trim());
  const canSubmit = idValid && !create.isPending;
  const errorKey = mutationErrorKey(create.error);

  function pickParty(p: PartySummary) {
    setSubjectPartyId(p.id);
    setSubjectLabel(`${p.firstName} ${p.lastName}`);
  }

  function submit() {
    const input: CreateDsarInput = {
      subjectPartyId: subjectPartyId.trim(),
      requestType,
      channel,
      ...(note.trim() !== "" ? { note: note.trim() } : {}),
    };
    create.mutate(input, {
      onSuccess: () => {
        setSubjectPartyId("");
        setSubjectLabel(null);
        setRequestType("ACCESS");
        setChannel("EMAIL");
        setNote("");
      },
    });
  }

  return (
    <Card className="cat-formcard" data-testid="dsar-form-card">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">
          <Icon name="member" size={16} strokeWidth={1.9} /> {t("dsar.create.title")}
        </span>
      </div>

      {canFindParty && <PartyPicker onPick={pickParty} />}

      <form
        data-testid="dsar-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={create.isPending}>
          <Input
            label={t("dsar.field.subject")}
            value={subjectPartyId}
            placeholder="00000000-0000-0000-0000-000000000000"
            hint={
              subjectLabel
                ? t("dsar.field.subjectPicked") + subjectLabel
                : t("dsar.field.subjectHint")
            }
            onChange={(e) => {
              setSubjectPartyId(e.target.value);
              setSubjectLabel(null);
            }}
            data-testid="dsar-subject"
          />
          <Select
            label={t("dsar.field.type")}
            value={requestType}
            onChange={(e) => setRequestType(e.target.value as DsarRequestType)}
            data-testid="dsar-type"
          >
            {DSAR_REQUEST_TYPES.map((code) => (
              <option key={code} value={code}>
                {t(TYPE_LABEL_KEY[code])}
              </option>
            ))}
          </Select>
          <Select
            label={t("dsar.field.channel")}
            value={channel}
            onChange={(e) => setChannel(e.target.value as DsarChannel)}
            data-testid="dsar-channel"
          >
            {DSAR_CHANNELS.map((code) => (
              <option key={code} value={code}>
                {t(CHANNEL_LABEL_KEY[code])}
              </option>
            ))}
          </Select>
          <Input
            label={t("dsar.field.note")}
            value={note}
            placeholder={t("dsar.field.notePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
            data-testid="dsar-note"
          />
        </fieldset>

        <p className="muted surf-note" data-testid="dsar-due-info">
          <Icon name="calendar" size={13} strokeWidth={2} /> {t("dsar.create.dueInfo")}
        </p>

        {requestType === "ERASURE" && (
          <p className="muted surf-note" data-testid="dsar-erasure-note">
            <Icon name="info" size={13} strokeWidth={2} /> {t("dsar.erasure.note")}
          </p>
        )}

        <div className="k-form-actions">
          <Button
            variant="primary"
            type="submit"
            disabled={!canSubmit}
            loading={create.isPending}
            data-testid="dsar-submit"
          >
            {t("dsar.create.submit")}
          </Button>
          {errorKey && (
            <span role="alert" className="form-error" data-testid="dsar-create-error">
              {t(errorKey)}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// The optional subject picker — a name/phone lookup against /v1/parties/find (party:read).
// Selecting a match fills the subjectPartyId; the uuid input remains the source of truth.
function PartyPicker({ onPick }: { onPick: (p: PartySummary) => void }) {
  const { t } = useT();
  const [by, setBy] = useState<CustomerSearchBy>("name");
  const [draft, setDraft] = useState("");
  const [term, setTerm] = useState("");
  const search = useCustomerSearch(by, term);
  const rows = search.data?.parties ?? [];

  return (
    <div className="dsar-picker" data-testid="dsar-party-search">
      <form
        className="cust-search"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm(draft);
        }}
      >
        <fieldset className="k-form-grid">
          <Select
            label={t("dsar.picker.by")}
            value={by}
            onChange={(e) => setBy(e.target.value as CustomerSearchBy)}
          >
            <option value="name">{t("dsar.picker.byName")}</option>
            <option value="phone">{t("dsar.picker.byPhone")}</option>
          </Select>
          <Input
            label={t("dsar.picker.term")}
            value={draft}
            placeholder={by === "phone" ? "+90…" : t("dsar.picker.namePlaceholder")}
            onChange={(e) => setDraft(e.target.value)}
            data-testid="dsar-picker-input"
          />
        </fieldset>
        <div className="k-form-actions">
          <Button variant="secondary" type="submit" disabled={draft.trim() === ""}>
            {t("dsar.picker.search")}
          </Button>
        </div>
      </form>

      {term.trim() !== "" && rows.length > 0 && (
        <Table>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid="dsar-picker-row">
                <td className="cust-strong">
                  {p.firstName} {p.lastName}
                </td>
                <td>{p.phone ?? "—"}</td>
                <td className="cust-td-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    data-testid="dsar-picker-pick"
                    onClick={() => onPick(p)}
                  >
                    {t("dsar.picker.pick")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {term.trim() !== "" && !search.isLoading && rows.length === 0 && (
        <p className="muted surf-note">{t("dsar.picker.empty")}</p>
      )}
    </div>
  );
}

// The DSAR register list — org-scoped GET /v1/admin/dsar with server-side status/type
// filters and keyset "daha fazla" pagination. Honest loading / error / empty states;
// each row opens an inline manage panel (mirroring the customers/iam list→detail pattern).
function DsarList({ canWrite }: { canWrite: boolean }) {
  const { t, locale } = useT();
  const [status, setStatus] = useState<DsarStatus | "">("");
  const [type, setType] = useState<DsarRequestType | "">("");
  const [selected, setSelected] = useState<string | null>(null);

  const filter: DsarFilter = {
    ...(status ? { status } : {}),
    ...(type ? { requestType: type } : {}),
  };
  const list = useDsarList(filter, true);
  const rows: DsarRequest[] = list.data?.pages.flatMap((p) => p.data ?? []) ?? [];
  const selectedRow = rows.find((r) => r.id === selected) ?? null;

  return (
    <>
      <TableCard title={t("dsar.list.title")} data-testid="dsar-list">
        <form className="cust-search" onSubmit={(e) => e.preventDefault()}>
          <fieldset className="k-form-grid">
            <Select
              label={t("dsar.filter.status")}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as DsarStatus | "");
                setSelected(null);
              }}
              data-testid="dsar-filter-status"
            >
              <option value="">{t("dsar.filter.any")}</option>
              {DSAR_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(STATUS_LABEL_KEY[s])}
                </option>
              ))}
            </Select>
            <Select
              label={t("dsar.filter.type")}
              value={type}
              onChange={(e) => {
                setType(e.target.value as DsarRequestType | "");
                setSelected(null);
              }}
              data-testid="dsar-filter-type"
            >
              <option value="">{t("dsar.filter.any")}</option>
              {DSAR_REQUEST_TYPES.map((code) => (
                <option key={code} value={code}>
                  {t(TYPE_LABEL_KEY[code])}
                </option>
              ))}
            </Select>
          </fieldset>
        </form>

        {list.isLoading && <LoadingState label={t("dsar.list.loading")} rows={3} />}
        {list.error instanceof ProblemError && (
          <ErrorState title={t("dsar.list.error")} description={list.error.problem.detail} />
        )}
        {!list.isLoading && !list.error && rows.length === 0 && (
          <EmptyState data-testid="dsar-empty" icon="member" title={t("dsar.list.empty")} />
        )}
        {rows.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("dsar.col.subject")}</th>
                  <th>{t("dsar.col.type")}</th>
                  <th>{t("dsar.col.status")}</th>
                  <th>{t("dsar.col.received")}</th>
                  <th>{t("dsar.col.due")}</th>
                  <th className="cust-td-right" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid="dsar-row">
                    <td className="cust-strong">
                      {r.subjectName ? (
                        <span data-testid="dsar-subject-name">{r.subjectName}</span>
                      ) : (
                        <span
                          className="dsar-mono muted"
                          title={t("dsar.subject.unresolved")}
                          data-testid="dsar-subject-id"
                        >
                          {r.subjectPartyId}
                        </span>
                      )}
                    </td>
                    <td>{t(TYPE_LABEL_KEY[r.requestType])}</td>
                    <td>
                      <StatusLabel tone={STATUS_TONE[r.status]}>
                        {t(STATUS_LABEL_KEY[r.status])}
                      </StatusLabel>
                    </td>
                    <td>{fmtDate(r.receivedAt, locale)}</td>
                    <td>
                      {fmtDate(r.dueAt, locale)}
                      {isOverdue(r) && (
                        <span className="dsar-overdue" data-testid="dsar-overdue">
                          <StatusLabel tone="expiring" dot={false}>
                            {t("dsar.overdue")}
                          </StatusLabel>
                        </span>
                      )}
                    </td>
                    <td className="cust-td-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        data-testid="dsar-manage-btn"
                        onClick={() => setSelected((cur) => (cur === r.id ? null : r.id))}
                      >
                        {t("dsar.manage")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {list.hasNextPage && (
              <div className="k-form-actions">
                <Button
                  variant="secondary"
                  type="button"
                  data-testid="dsar-more"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {t("dsar.list.more")}
                </Button>
              </div>
            )}
          </>
        )}
      </TableCard>

      {selectedRow && <DsarManageCard key={selectedRow.id} row={selectedRow} canWrite={canWrite} />}
    </>
  );
}

// The per-request manage panel: the full record + the forward-legal status transitions
// (RECEIVED → IN_PROGRESS/REJECTED, IN_PROGRESS → FULFILLED/REJECTED). Terminal states
// offer no moves. Gated on dsar:write. An ERASURE request is the exception: FULFILLED is
// NOT offered by hand (see `manualTransitions`) — it is reached only by executing the
// crypto-shred in the ErasureLane below.
function DsarManageCard({ row, canWrite }: { row: DsarRequest; canWrite: boolean }) {
  const { t, locale } = useT();
  const update = useUpdateDsarStatus(row.id);
  const [toast, setToast] = useState(false);

  const isErasure = row.requestType === "ERASURE";
  const moves = manualTransitions(row);
  const errorKey = mutationErrorKey(update.error);

  function transition(to: DsarStatus) {
    update.mutate({ status: to }, { onSuccess: () => setToast(true) });
  }

  return (
    <>
      <Card className="cust-detailcard" data-testid="dsar-managecard">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">
            <Icon name="member" size={16} strokeWidth={1.9} /> {t(TYPE_LABEL_KEY[row.requestType])}
          </span>
          <StatusLabel tone={STATUS_TONE[row.status]}>
            {t(STATUS_LABEL_KEY[row.status])}
          </StatusLabel>
        </div>

        <fieldset className="k-form-grid" disabled>
          <Input
            label={t("dsar.field.subject")}
            value={row.subjectName ?? row.subjectPartyId}
            hint={row.subjectName ? row.subjectPartyId : t("dsar.subject.unresolved")}
            readOnly
          />
          <Input
            label={t("dsar.field.channel")}
            value={t(CHANNEL_LABEL_KEY[row.channel])}
            readOnly
          />
          <Input label={t("dsar.col.received")} value={fmtDate(row.receivedAt, locale)} readOnly />
          <Input label={t("dsar.col.due")} value={fmtDate(row.dueAt, locale)} readOnly />
          <Input
            label={t("dsar.field.fulfilled")}
            value={fmtDate(row.fulfilledAt, locale)}
            readOnly
          />
          <Input label={t("dsar.field.note")} value={row.note ?? "—"} readOnly />
        </fieldset>

        {isErasure && (
          <p className="muted surf-note" data-testid="dsar-erasure-shred">
            <Icon name="info" size={13} strokeWidth={2} /> {t("dsar.erasure.shred")}
          </p>
        )}

        {/* The erasure receipt — present ONLY when the shred actually ran. Its
            absence on a FULFILLED erasure is exactly the state this slice
            eliminated, so it is worth showing on the record. */}
        {isErasure && row.erasureShredAuditId && (
          <p className="muted surf-note" data-testid="dsar-erasure-receipt">
            <Icon name="lock" size={13} strokeWidth={2} /> {t("dsar.erasure.receipt")}:{" "}
            <span className="dsar-mono">{row.erasureShredAuditId}</span>
          </p>
        )}

        {isErasure && row.status !== "FULFILLED" && row.status !== "REJECTED" && (
          <ErasureLane row={row} canWrite={canWrite} />
        )}

        <div className="cust-reset">
          <div className="cust-reset__head">
            <span className="surf-cardtitle">{t("dsar.transition.title")}</span>
          </div>
          {moves.length === 0 ? (
            <p className="muted surf-note" data-testid="dsar-terminal">
              {t("dsar.transition.terminal")}
            </p>
          ) : canWrite ? (
            <div className="k-form-actions">
              {moves.map((to) => (
                <Button
                  key={to}
                  variant={to === "REJECTED" ? "secondary" : "primary"}
                  type="button"
                  loading={update.isPending}
                  data-testid={`dsar-transition-${to}`}
                  onClick={() => transition(to)}
                >
                  {t(STATUS_LABEL_KEY[to])}
                </Button>
              ))}
              {errorKey && (
                <span role="alert" className="form-error" data-testid="dsar-status-error">
                  {t(errorKey)}
                </span>
              )}
            </div>
          ) : (
            <p className="muted surf-note">{t("dsar.readOnly")}</p>
          )}
        </div>
      </Card>

      {toast && (
        <ToastStack>
          <Toast
            tone="success"
            title={t("dsar.transition.success")}
            closeLabel={t("dsar.toast.dismiss")}
            onClose={() => setToast(false)}
          />
        </ToastStack>
      )}
    </>
  );
}

/**
 * The KVKK erasure lane — the ONLY path to FULFILLED for an ERASURE request.
 *
 * Three gates, in order, each visible so the operator is never left guessing why
 * the button is inert:
 *   1. dsar:write (UX only; the API re-enforces).
 *   2. four-eyes — a `dsr.erase` approval bound to THIS DSAR id (SF1). The
 *      requester opens it here and a SECOND person grants it from their approver
 *      inbox; the server structurally excludes self-approval. Until it is
 *      APPROVED the execute button stays disabled with the waiting note.
 *   3. an explicit typed confirmation of irreversibility — the shred destroys
 *      keys, and a misfire cannot be undone by any amount of restore.
 *
 * Step-up (recent strong auth) is enforced server-side only; there is no
 * client-side way to assert it honestly, so a CSA-1007 is surfaced as its own
 * message rather than pre-empted with a guess.
 */
function ErasureLane({ row, canWrite }: { row: DsarRequest; canWrite: boolean }) {
  const { t } = useT();
  const requestApproval = useRequestErasureApproval();
  const execute = useExecuteErasure(row.id);
  const mine = useMyApprovals(canWrite);
  const [confirmText, setConfirmText] = useState("");
  const [receipt, setReceipt] = useState<EraseResult | null>(null);

  // The caller's own dsr.erase approval for THIS request (latest first) — the
  // same shape the config surface uses for config.activate.
  const approval = (mine.data ?? [])
    .filter((a) => a.action === DSR_ERASE_ACTION && a.resourceId === row.id)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
  const approved = approval?.status === "APPROVED";

  const confirmWord = t("dsar.erasure.confirmWord");
  const confirmed = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();
  const errorKey = mutationErrorKey(execute.error ?? requestApproval.error);

  if (!canWrite) {
    return (
      <div className="cust-reset" data-testid="dsar-erasure-lane">
        <p className="muted surf-note">{t("dsar.readOnly")}</p>
      </div>
    );
  }

  return (
    <div className="cust-reset" data-testid="dsar-erasure-lane">
      <div className="cust-reset__head">
        <span className="surf-cardtitle">
          <Icon name="lock" size={15} strokeWidth={1.9} /> {t("dsar.erasure.title")}
        </span>
      </div>

      <p className="muted surf-note" data-testid="dsar-erasure-explain">
        {t("dsar.erasure.explain")}
      </p>

      {receipt ? (
        <div className="dsar-erasure-receipt" data-testid="dsar-erasure-result">
          <StatusLabel tone="active">{t("dsar.erasure.done")}</StatusLabel>
          <p className="muted surf-note">
            {t("dsar.erasure.shredded")}: {receipt.shredded} · {t("dsar.erasure.restricted")}:{" "}
            {receipt.restricted} · {t("dsar.erasure.skipped")}: {receipt.skipped}
          </p>
          <p className="muted surf-note">
            {t("dsar.erasure.receipt")}: <span className="dsar-mono">{receipt.shredAuditId}</span>
          </p>
        </div>
      ) : (
        <>
          {!approval && (
            <div className="k-form-actions">
              <Button
                variant="secondary"
                type="button"
                data-testid="dsar-erasure-request-approval"
                loading={requestApproval.isPending}
                onClick={() =>
                  requestApproval.mutate({ action: DSR_ERASE_ACTION, resourceId: row.id })
                }
              >
                {t("dsar.erasure.requestApproval")}
              </Button>
              {/* The single-operator case, stated up front rather than discovered as a
                  403 after the request: dsr.erase needs a SECOND principal holding
                  dsr.erase:approve. Deliberate for an irreversible act. */}
              <span className="muted surf-note" data-testid="dsar-erasure-second-person">
                {t("dsar.erasure.noApprover")}
              </span>
            </div>
          )}

          {approval && !approved && (
            <p className="muted surf-note" data-testid="dsar-erasure-awaiting">
              <Icon name="lock" size={13} strokeWidth={1.9} /> {t("dsar.erasure.awaiting")}
            </p>
          )}

          {approved && (
            <>
              <Input
                label={t("dsar.erasure.confirmLabel")}
                value={confirmText}
                placeholder={confirmWord}
                hint={t("dsar.erasure.confirm")}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="dsar-erasure-confirm"
              />
              <div className="k-form-actions">
                <Button
                  variant="danger"
                  type="button"
                  data-testid="dsar-erasure-execute"
                  disabled={!confirmed || execute.isPending}
                  loading={execute.isPending}
                  onClick={() =>
                    execute.mutate(approval!.id, {
                      onSuccess: (res) => setReceipt(res.erasure),
                    })
                  }
                >
                  {t("dsar.erasure.execute")}
                </Button>
              </div>
            </>
          )}

          {errorKey && (
            <span role="alert" className="form-error" data-testid="dsar-erasure-error">
              {t(errorKey)}
            </span>
          )}
        </>
      )}
    </div>
  );
}
